/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: faxViewerApi.ts
 🗓️ Created: 2025-12-03
 🧩 Description:
    API client for Fax Viewer operations
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { FaxLog, FaxViewerQuery } from '../types';

/**
 * Fetch fax logs with pagination and filters
 */
export async function fetchFaxLogs(
  query: FaxViewerQuery,
  domainId?: number
): Promise<{
  data: FaxLog[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}> {
  const params = new URLSearchParams({
    page: query.page.toString(),
    limit: query.limit.toString(),
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  });

  if (query.search) params.append('search', query.search);
  if (query.faxDeviceId) params.append('faxDeviceId', query.faxDeviceId.toString());
  if (query.direction) params.append('direction', query.direction);
  if (query.success != null) params.append('success', query.success.toString());
  if (query.startDate) params.append('startDate', query.startDate);
  if (query.endDate) params.append('endDate', query.endDate);
  if (domainId != null) params.append('domainId', domainId.toString());

  const url = buildApiUrl(`/api/telephony/fax-viewer?${params.toString()}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch fax logs');
  }

  return await response.json();
}

/**
 * Fetch single fax log by ID
 */
export async function fetchFaxLogById(id: number): Promise<FaxLog> {
  const url = buildApiUrl(`/api/telephony/fax-viewer/${id}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch fax log');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete fax log
 */
export async function deleteFaxLog(id: number, deleteFiles: boolean = false): Promise<void> {
  const url = buildApiUrl(`/api/telephony/fax-viewer/${id}?deleteFiles=${deleteFiles}`);

  const response = await fetch(url, {
    method: 'DELETE',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete fax log');
  }
}

/**
 * Fetch a fax file with proper auth headers, returning a blob URL.
 * - inline=true  → returns blobUrl for use in a modal viewer (caller must revoke when done)
 * - inline=false → triggers browser download automatically, then revokes the blobUrl
 */
export async function downloadFaxFile(
  id: number | string,
  format: 'pdf' | 'tif' = 'pdf',
  inline: boolean = false
): Promise<string> {
  const url = buildApiUrl(`/api/telephony/fax-viewer/${id}/download?format=${format}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    let errMsg = 'Failed to download fax file';
    try {
      const err = await response.json();
      errMsg = err.message || errMsg;
    } catch { /* ignore */ }
    throw new Error(errMsg);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  if (!inline) {
    // Trigger automatic download then revoke
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `fax_${id}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  }

  // In both cases return the blobUrl:
  // - inline=true → used by caller for modal iframe (caller revokes on close)
  // - inline=false → already triggered download, blobUrl also returned but not typically used
  return blobUrl;
}

